import {Grid} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';

import {BLUE_LIGHT} from 'ui-library/Colors';
import * as Colors from 'ui-library/Colors';
import useDnDCanvasDragState from 'ui-library/drag-and-drop/hooks/useDnDCanvasDragState';
import useDocumentCursor from 'ui-library/hooks/useDocumentCursor';

import {useDataLayoutContext} from './DataLayoutContext';
import {GridSection as GridSectionType, NestedGrid, Tab} from './DataLayoutTypes';
import GridBlock, {gridBlockStyles} from './GridBlock';
import {isSameDraggableBlock} from './GridSectionDnDTypes';
import type {DraggableBlock} from './GridSectionDnDTypes';

interface GridSectionProps {
  gridSection: GridSectionType | NestedGrid | Tab;
  layoutPath: Array<string | number>;
  isInCombinedBlock?: boolean;
}

/** @package */
export default function GridSection({
  gridSection: {gridMode, rows},
  layoutPath,
  isInCombinedBlock = false,
}: GridSectionProps): JSX.Element {
  const {isEditable, onEvents} = useDataLayoutContext();
  const isCurrentBlockCombined = gridMode === 'COMBINED' || isInCombinedBlock;

  const handleMoveItem = useCallback(
    (source: DraggableBlock, target: DraggableBlock) => {
      onEvents?.([
        {
          type: 'MOVE_BLOCK',
          layoutPath,
          source: {row: source.row, col: source.col},
          target: {row: target.row, col: target.col},
        },
      ]);
    },
    [layoutPath, onEvents]
  );

  const {dragState, onItemDragStart, onItemHoverEnter, onItemHoverLeave} =
    useDnDCanvasDragState<DraggableBlock>({
      onMoveItem: handleMoveItem,
    });
  useDocumentCursor('grabbing', isEditable === true && dragState.isDragging);

  const draggableRows: Array<Array<DraggableBlock>> = useMemo(
    () => rows.map((row, rowIndex) => row.map((block, colIndex) => ({block, row: rowIndex, col: colIndex}))),
    [rows]
  );

  return (
    <Grid
      className={css(
        gridMode === 'COMBINED' && styles.combinedBlock,
        gridMode === 'COMBINED' && !isInCombinedBlock && gridBlockStyles.blockBorder,
        isInCombinedBlock && gridMode === 'COMBINED' && styles.nestedCombinedBlock,
        isEditable && dragState.isDragging && styles.noSelect
      )}
    >
      {draggableRows
        .map((row, rowIndex) =>
          row.map((draggableBlock, columnIndex) => {
            const {block} = draggableBlock;
            const isDraggable =
              isEditable === true && block.type !== 'PLACEHOLDER' && block.type !== 'NESTED_GRID';
            const isDropTarget = isEditable === true && block.type !== 'NESTED_GRID';
            const isDraggedItem =
              dragState.isDragging && isSameDraggableBlock(dragState.draggedItem, draggableBlock);
            const isHoveredTarget =
              dragState.isDragging &&
              dragState.isHovering &&
              isSameDraggableBlock(dragState.hoveredItem, draggableBlock) &&
              !isDraggedItem;

            return (
              <Grid.Col md={12 / row.length} key={`block-${rowIndex}-${columnIndex}`}>
                <DraggableGridBlock
                  draggableBlock={draggableBlock}
                  layoutPath={[...layoutPath, 'rows', rowIndex, columnIndex]}
                  isInCombinedBlock={isCurrentBlockCombined}
                  isDraggable={isDraggable}
                  isDropTarget={isDropTarget}
                  isDraggedItem={isDraggedItem}
                  isHoveredTarget={isHoveredTarget}
                  dragState={dragState}
                  onDragStart={onItemDragStart}
                  onHoverEnter={onItemHoverEnter}
                  onHoverLeave={onItemHoverLeave}
                />
              </Grid.Col>
            );
          })
        )
        .flat()}
    </Grid>
  );
}

interface DraggableGridBlockProps {
  draggableBlock: DraggableBlock;
  layoutPath: Array<string | number>;
  isInCombinedBlock: boolean;
  isDraggable: boolean;
  isDropTarget: boolean;
  isDraggedItem: boolean;
  isHoveredTarget: boolean;
  dragState: ReturnType<typeof useDnDCanvasDragState<DraggableBlock>>['dragState'];
  onDragStart: ReturnType<typeof useDnDCanvasDragState<DraggableBlock>>['onItemDragStart'];
  onHoverEnter: ReturnType<typeof useDnDCanvasDragState<DraggableBlock>>['onItemHoverEnter'];
  onHoverLeave: ReturnType<typeof useDnDCanvasDragState<DraggableBlock>>['onItemHoverLeave'];
}

function DraggableGridBlock({
  draggableBlock,
  layoutPath,
  isInCombinedBlock,
  isDraggable,
  isDropTarget,
  isDraggedItem,
  isHoveredTarget,
  dragState,
  onDragStart,
  onHoverEnter,
  onHoverLeave,
}: DraggableGridBlockProps): JSX.Element {
  const {block} = draggableBlock;
  const handleMouseMove = useCallback(
    (_e: React.MouseEvent) => {
      if (!dragState.isDragging || !isDropTarget) {
        return;
      }
      onHoverEnter(draggableBlock, 'right');
    },
    [dragState.isDragging, isDropTarget, draggableBlock, onHoverEnter]
  );

  const handleMouseLeave = useCallback(() => {
    if (!dragState.isDragging) {
      return;
    }
    onHoverLeave();
  }, [dragState.isDragging, onHoverLeave]);

  if (block.type === 'NESTED_GRID') {
    return <GridSection gridSection={block} layoutPath={layoutPath} isInCombinedBlock={isInCombinedBlock} />;
  }

  return (
    <div
      className={css(
        styles.draggableWrapper,
        isDraggedItem && styles.draggedBlock,
        isHoveredTarget && styles.hoveredTarget
      )}
      style={
        isDraggedItem && dragState.isDragging
          ? {
              transform: `translate3d(${dragState.dragDelta.translateX}px, ${dragState.dragDelta.translateY}px, 0)`,
            }
          : undefined
      }
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <GridBlock
        block={block}
        layoutPath={layoutPath}
        hasBorder={!isInCombinedBlock}
        isDraggable={isDraggable}
        draggableBlock={draggableBlock}
        onDragStart={onDragStart}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  combinedBlock: {
    backgroundColor: Colors.WHITE,
    // Need to explicitly set margin to 0, otherwise the blocks aren't aligned in the grid
    margin: 0,
  },
  nestedCombinedBlock: {
    backgroundColor: Colors.WHITE,
  },
  noSelect: {
    userSelect: 'none',
  },
  draggableWrapper: {
    position: 'relative',
    height: '100%',
    paddingBottom: 8,
  },
  draggedBlock: {
    opacity: 0.7,
    zIndex: 100,
    pointerEvents: 'none',
  },
  hoveredTarget: {
    outline: `2px solid ${BLUE_LIGHT}`,
    outlineOffset: -2,
    borderRadius: 4,
  },
});
